import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { hooks } from '../hooks';
import { text } from '../text';
import { utils } from '../utils';
import { theme } from '../constants';
import { components } from '../components';
import axiosInstance from '../services/axiosInstance';
import { RootState } from '../store';
import { useLanguage } from '../context/LanguageContext';
import { formatDateTime } from '../utils/formatText';
import {
  MdCheckCircle, MdCancel, MdPendingActions, MdError, MdWarning,
} from 'react-icons/md';

const CheckCircleIcon = MdCheckCircle as any;
const CancelIcon = MdCancel as any;
const PendingActionsIcon = MdPendingActions as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;

const statusColor: Record<string, string> = {
  pending: '#F59E0B',
  approved: '#10B981',
  rejected: '#EF4444',
};

const formatRp = (val: any) =>
  `Rp ${Number(val || 0).toLocaleString('id-ID')}`;

const formatDate = (d: string) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const TravelExpenseDetail: React.FC = () => {
  const { search } = useLocation();
  const navigate = hooks.useAppNavigate();
  const authSlice = hooks.useAppSelector((state: RootState) => state.authSlice);
  const { t } = useLanguage();

  const userPermissions = (authSlice as any).permissions || [];
  const canApprove = userPermissions.includes('travel_expense:approve');

  const searchParams = new URLSearchParams(search);
  const expId = searchParams.get('id');

  const [expense, setExpense] = useState<any>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [modal, setModal] = useState<'approve' | 'reject' | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const fetchExpense = async () => {
    if (!expId) { setIsFetching(false); return; }
    try {
      const res = await axiosInstance.get(`/travel-expenses/${expId}`);
      setExpense(res.data);
    } catch {
      setAlert({ type: 'error', msg: 'Gagal memuat data pengajuan' });
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => { fetchExpense(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async (action: 'approve' | 'reject') => {
    if (!expId) return;
    setActionLoading(true);
    try {
      const url = `/travel-expenses/${expId}/${action}`;
      await axiosInstance.put(url, { notes });
      setModal(null);
      setNotes('');
      setAlert({ type: 'success', msg: action === 'approve' ? 'Pengajuan disetujui' : 'Pengajuan ditolak' });
      await fetchExpense();
    } catch (e: any) {
      setAlert({ type: 'error', msg: e.response?.data?.message || 'Aksi gagal' });
    } finally {
      setActionLoading(false);
    }
  };

  const StatusBadge = ({ status }: { status: string }) => (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      color: '#fff',
      backgroundColor: statusColor[status] || '#9CA3AF',
      textTransform: 'capitalize',
    }}>
      {status}
    </span>
  );

  const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <text.T12 style={{ color: '#9CA3AF' }}>{label}</text.T12>
      <text.T14 style={{ color: theme.colors.mainDark, fontWeight: '600' }}>{value || '-'}</text.T14>
    </div>
  );

  return (
    <div id="screen" style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <components.Header title={(t as any)?.travelExpenses?.detailTitle || 'Detail Uang Jalan'} goBack={true} />

      <main className="container" style={{ marginTop: 60, paddingBottom: 110, height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
        <div style={{ padding: '16px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Alert */}
          {alert && (
            <div style={{
              backgroundColor: alert.type === 'success' ? '#D1FAE5' : '#FEE2E2',
              border: `1px solid ${alert.type === 'success' ? '#6EE7B7' : '#FECACA'}`,
              borderRadius: 10,
              padding: '10px 14px',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {alert.type === 'success'
                ? <CheckCircleIcon size={18} color="#10B981" />
                : <ErrorIcon size={18} color="#EF4444" />}
              <text.T14 style={{ color: alert.type === 'success' ? '#065F46' : '#DC2626' }}>
                {alert.msg}
              </text.T14>
            </div>
          )}

          {isFetching ? (
            <text.T16 style={{ textAlign: 'center', marginTop: 40, color: '#9CA3AF' }}>
              {t.common.loading}
            </text.T16>
          ) : !expense ? (
            <text.T16 style={{ textAlign: 'center', marginTop: 40, color: '#9CA3AF' }}>
              Data tidak ditemukan
            </text.T16>
          ) : (
            <>
              {/* Header card */}
              <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 16, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ ...utils.rowCenterSpcBtw(), marginBottom: 12 }}>
                  <text.H5 style={{ fontSize: 15 }}>#{expense.id} • {expense.from_location} → {expense.to_location}</text.H5>
                  <StatusBadge status={expense.status} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <InfoRow label="Pengaju" value={expense.created_by_name} />
                  <InfoRow label="Departemen" value={expense.employee_department} />
                  <InfoRow label="Pemberi Tugas" value={expense.task_assigner_name} />
                  <InfoRow label="Klien" value={expense.client_name} />
                  <InfoRow label="Kapal" value={expense.vessel_name} />
                  <InfoRow label="Diajukan" value={formatDateTime(expense.created_at)} />
                  <InfoRow label="Berangkat" value={formatDate(expense.departure_date)} />
                  <InfoRow label="Kembali" value={formatDate(expense.return_date)} />
                </div>
              </div>

              {/* Cost items */}
              <div style={{ backgroundColor: '#fff', borderRadius: 14, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
                  <text.H5 style={{ fontSize: 14 }}>Rincian Biaya</text.H5>
                </div>
                {(expense.items || []).length === 0 ? (
                  <text.T14 style={{ padding: 16, color: '#9CA3AF' }}>Tidak ada rincian biaya</text.T14>
                ) : (
                  <>
                    <div style={{ padding: '8px 16px', backgroundColor: '#F9FAFB', display: 'flex', borderBottom: '1px solid #E5E7EB' }}>
                      <text.T12 style={{ flex: 1, color: '#6B7280', fontWeight: '700' }}>Jenis</text.T12>
                      <text.T12 style={{ color: '#6B7280', fontWeight: '700' }}>Nominal</text.T12>
                    </div>
                    {(expense.items || []).map((item: any, i: number) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #F9FAFB' }}>
                        <text.T14 style={{ color: theme.colors.mainDark }}>{item.cost_type}</text.T14>
                        <text.T14 style={{ color: theme.colors.mainDark, fontWeight: '600' }}>{formatRp(item.amount)}</text.T14>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: '#F0FDF4', borderTop: '1px solid #D1FAE5' }}>
                      <text.T14 style={{ fontWeight: '700', color: '#065F46' }}>Total</text.T14>
                      <text.T14 style={{ fontWeight: '700', color: '#059669' }}>{formatRp(expense.total_amount)}</text.T14>
                    </div>
                  </>
                )}
              </div>

              {/* Review info */}
              {expense.status !== 'pending' && (
                <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 16, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <text.H5 style={{ fontSize: 14, marginBottom: 4 }}>Info Persetujuan</text.H5>
                  <InfoRow label={expense.status === 'approved' ? 'Disetujui oleh' : 'Ditolak oleh'} value={expense.reviewed_by_name} />
                  <InfoRow label="Tanggal" value={formatDateTime(expense.reviewed_at)} />
                  {expense.review_notes && <InfoRow label="Catatan" value={expense.review_notes} />}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Action buttons — only shown to approvers when status is pending */}
      {canApprove && expense?.status === 'pending' && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, maxWidth: 650, margin: '0 auto',
          padding: '12px 16px', backgroundColor: '#fff', borderTop: '1px solid #F3F4F6',
          display: 'flex', gap: 10,
        }}>
          <button
            onClick={() => setModal('reject')}
            style={{
              flex: 1, padding: '12px 0', borderRadius: 12, border: '1.5px solid #EF4444',
              backgroundColor: '#fff', color: '#EF4444', fontWeight: '700', fontSize: 15, cursor: 'pointer',
            }}
          >
            Tolak
          </button>
          <button
            onClick={() => setModal('approve')}
            style={{
              flex: 1, padding: '12px 0', borderRadius: 12, border: 'none',
              backgroundColor: '#10B981', color: '#fff', fontWeight: '700', fontSize: 15, cursor: 'pointer',
            }}
          >
            Setujui
          </button>
        </div>
      )}

      {/* Approve / Reject modal */}
      {modal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => { if (!actionLoading) setModal(null); }}>
          <div
            style={{
              width: '100%', maxWidth: 650, backgroundColor: '#fff',
              borderTopLeftRadius: 20, borderTopRightRadius: 20,
              padding: '20px 16px 32px',
            }}
            onClick={e => e.stopPropagation()}
          >
            <text.H5 style={{ fontSize: 16, marginBottom: 4 }}>
              {modal === 'approve' ? 'Setujui Pengajuan?' : 'Tolak Pengajuan?'}
            </text.H5>
            <text.T14 style={{ color: '#6B7280', marginBottom: 14 }}>
              {modal === 'approve'
                ? 'Pengaju akan mendapat notifikasi bahwa pengajuannya disetujui.'
                : 'Pengaju akan mendapat notifikasi bahwa pengajuannya ditolak.'}
            </text.T14>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Catatan (opsional)"
              rows={3}
              style={{
                width: '100%', border: '1px solid #D1D5DB', borderRadius: 10,
                padding: '10px 14px', fontSize: 15, boxSizing: 'border-box',
                outline: 'none', fontFamily: 'inherit', resize: 'none',
                color: theme.colors.mainDark, marginBottom: 14,
              }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { if (!actionLoading) setModal(null); }}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 12, border: '1.5px solid #D1D5DB',
                  backgroundColor: '#fff', color: '#6B7280', fontWeight: '600', fontSize: 15, cursor: 'pointer',
                }}
              >
                Batal
              </button>
              <button
                onClick={() => handleAction(modal)}
                disabled={actionLoading}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 12, border: 'none',
                  backgroundColor: modal === 'approve' ? '#10B981' : '#EF4444',
                  color: '#fff', fontWeight: '700', fontSize: 15,
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  opacity: actionLoading ? 0.7 : 1,
                }}
              >
                {actionLoading ? 'Memproses...' : modal === 'approve' ? 'Ya, Setujui' : 'Ya, Tolak'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
